import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Users, MapPin, Target, Shield, Award, Heart, Bot, Clock, Globe, Star, DollarSign, Zap, CheckCircle, TrendingUp, MessageSquare } from 'lucide-react';

const About = () => {
  const handleBookDemo = () => {
    alert('Demo booking functionality - will integrate with Calendly or similar booking system');
  };

  const results = [
    { number: "50+", label: "Restaurants Trust PrismoSpark", icon: Users },
    { number: "8,500+", label: "Conversations by Isabella AI", icon: MessageSquare },
    { number: "850+", label: "Five-Star Reviews Generated", icon: Star },
    { number: "380,000+", label: "Website Visitors Driven", icon: Globe },
    { number: "34%", label: "Avg Booking Increase (90 Days)", icon: TrendingUp },
    { number: "4.8/5", label: "Client Satisfaction Rating", icon: Award }
  ];

  const differentiators = [
    {
      title: "Restaurant-Specific AI",
      description: "Isabella isn't ChatGPT with a restaurant prompt. She's trained on thousands of restaurant conversations. She understands \"table by the window,\" \"do you have vegan options,\" and \"what's your spice level\" in 6 languages.",
      icon: Bot
    },
    {
      title: "Real-Time Booking Intelligence",
      description: "We integrate directly with your reservation system. Isabella knows real-time availability—no double bookings, no manual updates.",
      icon: Clock
    },
    {
      title: "Honest Pricing",
      description: "Our pricing is on the website. Setup fees disclosed upfront. No \"call for pricing\" games. No bait-and-switch.",
      icon: Shield
    },
    {
      title: "Fast Deployment",
      description: "Growth Accelerator and Complete Restaurant System both launch in 2-3 weeks. No three-month implementation nightmares.",
      icon: Zap
    },
    {
      title: "Skin in the Game",
      description: "30-day money-back guarantee because we know the system works. If you don't see results, we don't deserve your money.",
      icon: CheckCircle
    }
  ];

  const globalMarkets = [
    {
      flag: "🇨🇦",
      country: "Canada",
      cities: "Toronto, Vancouver, Montreal, Calgary",
      expertise: "Bilingual needs (English/French), competitive urban dining scenes, seasonal tourism patterns"
    },
    {
      flag: "🇺🇸", 
      country: "United States",
      cities: "New York, Los Angeles, Seattle, Austin, Miami, Chicago",
      expertise: "Diverse demographics, intense competition, review-driven decisions, high search volume"
    },
    {
      flag: "🇮🇳",
      country: "India", 
      cities: "Mumbai, Delhi, Ludhiana, Pune, Hyderabad",
      expertise: "Multilingual communication (Hindi/Punjabi/Tamil/etc.), value-conscious customers, mobile-first users, WhatsApp preference"
    }
  ];

  const restaurantChallenges = [
    "Perishable inventory: Empty tables tonight can never be sold tomorrow",
    "Real-time decision-making: You need to know what's working NOW, not last month", 
    "Reputation sensitivity: One bad review can cost 30+ bookings",
    "Multilingual needs: Customers speak dozens of languages",
    "After-hours opportunities: 40% of searches happen when you're closed"
  ];

  const approach = [
    {
      title: "Transparency First",
      description: "Pricing shown upfront. No hidden fees. No bait-and-switch.",
      icon: Shield
    },
    {
      title: "Results Over Vanity Metrics",
      description: "We care about reservations, reviews, rankings. Not vanity metrics.",
      icon: Target
    },
    {
      title: "Partnership Model", 
      description: "We only succeed when you succeed. That's why 89% of clients stay with us past year one.",
      icon: Heart
    },
    {
      title: "No-Pressure Sales",
      description: "Our demo calls are educational, not pushy. We'll tell you honestly if PrismoSpark isn't the right fit.",
      icon: Users
    }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal flex items-center">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#2D6A4F]/20 to-[#2D6A4F]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <Badge className="inline-block mb-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-3">
              About PrismoSpark
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Helping Independent Restaurants
              <span className="block bg-gradient-to-r from-[#2D6A4F] to-[#2D6A4F] bg-clip-text text-transparent animate-gradient bg-300%">
                Compete Online
              </span>
            </h1>
            <h2 className="text-2xl lg:text-3xl font-semibold text-brand-ivory/90 mb-16 leading-relaxed">
              Without the Enterprise Price Tag
            </h2>
            
            {/* Social Proof Badge */}
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-4 text-white group hover:bg-white/20 transition-all duration-500">
              <div className="flex text-brand-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} fill="currentColor" className="animate-pulse" style={{animationDelay: `${i * 0.2}s`}} />
                ))}
              </div>
              <span className="font-medium text-lg">50+ restaurants trust us • Founded in 2025 • 4.8/5 rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-12">Our Mission</h2>
            <div className="space-y-8 text-xl text-brand-charcoal-600 leading-relaxed">
              <p>
                Independent restaurants deserve the same powerful technology as corporate chains — AI automation, SEO optimization, reputation management, data analytics — without enterprise complexity or million-dollar budgets.
              </p>
              <p className="text-2xl font-semibold text-brand-charcoal">
                Every restaurant should grow like the big chains—without needing their massive budgets.
              </p>
              <p>
                So we built a platform focused exclusively on restaurants. Not generic marketing software. Not one-size-fits-all solutions. Just tools that drive what matters: reservations, reviews, and revenue.
              </p>
              <p className="text-lg font-semibold text-[#2D6A4F]">
                Founded in 2025, PrismoSpark exists to level the playing field for independent restaurants everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-32 bg-brand-ivory-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-8">Our Story</h2>
              <div className="space-y-6 text-lg text-brand-charcoal-600 leading-relaxed">
                <p>
                  PrismoSpark was founded in 2025 to solve a problem we saw everywhere: independent restaurants struggling to compete online while chain competitors dominated with sophisticated technology.
                </p>
                <p>
                  The chains had AI customer service, automated reputation management, SEO teams, and analytics dashboards. Independent restaurants had... a Facebook page updated once a month.
                </p>
                <p className="text-xl font-semibold text-brand-charcoal">
                  That gap shouldn't exist.
                </p>
                <p>
                  So we built what independent restaurants actually need: enterprise-level technology at prices that make sense. No complexity. No jargon. No locked-in contracts with hidden fees.
                </p>
                <p className="text-lg font-semibold text-brand-teal">
                  Just tools that drive what matters: reservations, reviews, and revenue.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl p-12 text-white">
              <h3 className="text-2xl font-bold mb-8">The Problem We Saw</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-brand-sienna rounded-full mt-2 flex-shrink-0"></div>
                  <p>Chain restaurants: AI customer service, automated reviews, SEO teams</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-brand-sienna rounded-full mt-2 flex-shrink-0"></div>
                  <p>Independent restaurants: Facebook page updated monthly</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-[#2D6A4F] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="font-semibold">Our Solution: Level the playing field</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Only Restaurants */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">Why Only Restaurants?</h2>
            <p className="text-xl text-brand-charcoal-600 max-w-4xl mx-auto leading-relaxed">
              We could have built generic marketing software for all businesses. But restaurants have unique challenges that generic tools can't solve:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {restaurantChallenges.map((challenge, index) => (
              <Card key={index} className="p-8 border-2 border-brand-charcoal/20 hover:border-[#2D6A4F]/50 transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-0">
                  <p className="text-brand-charcoal-600 leading-relaxed">{challenge}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-brand-charcoal mb-4">
              We understand your challenges: thin margins, high competition, staffing shortages.
            </p>
            <p className="text-xl text-brand-charcoal-600 mb-4">
              We know your customers: what makes hungry people click "reserve" vs. bouncing to competitors.
            </p>
            <p className="text-xl text-brand-charcoal-600">
              We speak your language: no confusing tech jargon, just real solutions for real problems.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-32 bg-brand-ivory-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">
              What Makes PrismoSpark Different
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {differentiators.map((item, index) => (
              <Card key={index} className="group overflow-hidden shadow-xl border-2 border-brand-charcoal/20 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <CardHeader className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <item.icon className="text-white" size={28} />
                    </div>
                    <CardTitle className="text-2xl text-brand-charcoal group-hover:text-[#2D6A4F] transition-colors duration-300">
                      {item.title}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-brand-charcoal-600 text-lg leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Results */}
      <section className="py-32 bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
              Our Results
              <span className="block text-2xl lg:text-3xl font-normal text-brand-ivory/80 mt-4">(The Numbers)</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {results.map((stat, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-4 group">
                <CardHeader className="text-center p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <stat.icon className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-4xl font-bold text-white mb-4">{stat.number}</CardTitle>
                  <CardDescription className="text-brand-ivory/80 text-lg font-medium">{stat.label}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">
              Global Reach, Local Expertise
            </h2>
            <p className="text-xl text-brand-charcoal-600 max-w-4xl mx-auto">
              We serve restaurants in three countries because we understand their unique markets:
            </p>
          </div>

          <div className="space-y-8">
            {globalMarkets.map((market, index) => (
              <Card key={index} className="overflow-hidden shadow-xl border-2 border-brand-charcoal/20">
                <div className="grid lg:grid-cols-12">
                  <div className="lg:col-span-4 bg-gradient-to-br from-brand-charcoal to-brand-charcoal-700 p-8 text-white flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">{market.flag}</div>
                      <h3 className="text-2xl font-bold mb-2">{market.country}</h3>
                      <p className="text-brand-ivory/80">{market.cities}</p>
                    </div>
                  </div>
                  <div className="lg:col-span-8 p-8 flex items-center">
                    <div>
                      <h4 className="text-xl font-bold text-brand-charcoal mb-4">Market Expertise:</h4>
                      <p className="text-brand-charcoal-600 text-lg leading-relaxed">{market.expertise}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-32 bg-brand-ivory-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8">Our Approach</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {approach.map((item, index) => (
              <Card key={index} className="p-8 border-2 border-brand-charcoal/20 hover:border-[#2D6A4F]/50 transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader className="p-0 mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-lg flex items-center justify-center">
                      <item.icon className="text-white" size={20} />
                    </div>
                    <CardTitle className="text-xl text-brand-charcoal">{item.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <p className="text-brand-charcoal-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Card className="max-w-4xl mx-auto p-12 bg-gradient-to-r from-[#2D6A4F]/10 to-[#2D6A4F]/20 border-2 border-[#2D6A4F]/30">
              <CardContent className="p-0">
                <h3 className="text-2xl font-bold text-brand-charcoal mb-6">Partnership Model</h3>
                <p className="text-lg text-brand-charcoal-600 leading-relaxed mb-6">
                  We only succeed when you succeed. That's why 89% of clients stay with us past year one. When your revenue grows, you keep us. When it doesn't, you leave. Simple.
                </p>
                <p className="text-xl font-semibold text-[#2D6A4F]">
                  We'd rather have 50 successful restaurants than 400 mediocre ones.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-[#2D6A4F] via-[#2D6A4F] to-[#2D6A4F] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            Ready to Level the Playing Field?
          </h2>
          <p className="text-xl text-[#2D6A4F] mb-12 leading-relaxed">
            Our demo calls are educational, not pushy. We'll tell you honestly if PrismoSpark isn't the right fit. Book a consultation with our restaurant growth experts.
          </p>
          
          <Button 
            onClick={handleBookDemo}
            className="group relative bg-[#2D6A4F] hover:bg-[#2D6A4F] text-white font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl shadow-[#2D6A4F]/25 hover:shadow-[#2D6A4F]/40 transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
          >
            {/* Animated background layer */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center space-x-3">
              <Users className="w-6 h-6 group-hover:animate-pulse" />
              <span>Book Free Growth Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-[#0A7A6E]/50 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500 -z-10 scale-110"></div>
          </Button>
          
          <p className="text-[#2D6A4F] mt-6">
            30-minute session • Personalized for your restaurant • No sales pressure
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;